import Logo from "@/components/Logo";
import Button from "@/components/Button";

import { Permissions, RequestPermissions } from "@/app/types";
import PermissionLine from "@/app/pages/LobbyPage/PermissionLine";
import LocationPermissionLine from "@/app/pages/LobbyPage/LocationPermissionLine";
import LobbyVideo from "@/app/pages/LobbyPage/LobbyVideo";
import ConsentNotice from "@/app/pages/LobbyPage/ConsentNotice";


interface ILobbyPageProps {
  startTime: string;
  address: string;
  customerName?: string;
  staffName?: string;
  permissions: Permissions;
  requestPermissions: RequestPermissions;
  videoStream?: MediaStream;
  onJoin(): void;
  isClient: boolean;
}

// The app's type scale is base/sm/xs plus a 22px "3xl", so hierarchy comes from
// weight and colour rather than size: a small uppercase label over full-size
// content, the way grouped lists read on both phone platforms.
const SECTION_LABEL = "text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400";
const MUTED = "text-gray-500 dark:text-gray-400";


export default function LobbyPage(
  {
    startTime,
    address,
    customerName,
    staffName,
    permissions,
    requestPermissions,
    videoStream,
    onJoin,
    isClient,
  }: ILobbyPageProps
) {
  const joinButtonText = isClient ? "Join & Consent" : "Join call";
  const meetingWith = isClient ? staffName : customerName;

  const joinButton = (
    <Button variant="primary" onClick={onJoin} size="full" className="h-12 text-base">
      {joinButtonText}
    </Button>
  );

  // A customer whose microphone is off can still join — the call is simply
  // one-way until they fix it, and saying so beats a button that only looks
  // broken.
  const microphoneWarning = !permissions.audio && (
    <p className="mt-2 text-sm text-orange-base">
      Your microphone is off — turn it on so you can be heard.
    </p>
  );

  const detailRows = [
    { label: "Address", value: address },
    ...(isClient ? [] : [{ label: "Customer", value: customerName }]),
    { label: "Sales rep", value: staffName },
  ];

  return (
    <div className="relative box-border flex h-max min-h-screen w-full flex-col items-start bg-white px-5 pt-6 font-default leading-normal text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      <Logo />

      <section className="mt-8 grid w-full max-w-5xl grid-cols-1 gap-10 pb-8 md:grid-cols-12 lg:gap-16">
        <div className="hidden flex-col md:col-span-7 md:flex lg:col-span-8">
          <div className="flex flex-1 items-start">
            <LobbyVideo videoStream={videoStream} />
          </div>
          <div className="hidden pb-6 pt-3 md:max-lg:block">
            {joinButton}
            {microphoneWarning}
          </div>
        </div>

        <div className="flex w-full max-w-lg flex-col gap-8 md:col-span-5 md:max-w-none lg:col-span-4">
          <header>
            <h1 className="font-heading text-3xl font-bold leading-tight text-gray-900 dark:text-gray-100">
              Live Video Estimate
            </h1>
            <p className="mt-1 text-base">{startTime}</p>
            {meetingWith && <p className={`mt-1 text-sm ${MUTED}`}>with {meetingWith}</p>}
          </header>

          <div className="md:hidden">
            <LobbyVideo videoStream={videoStream} />
          </div>

          {isClient && <ConsentNotice />}

          <div className="hidden lg:block">
            {joinButton}
            {microphoneWarning}
          </div>

          <section>
            <h2 className={SECTION_LABEL}>Check permissions</h2>
            <div className="mt-2 flex flex-col gap-2 text-sm">
              <PermissionLine
                permission={"video"}
                hasPermission={permissions.video}
                requestPermissions={requestPermissions}
              />
              <PermissionLine
                permission={"audio"}
                hasPermission={permissions.audio}
                requestPermissions={requestPermissions}
              />
              {isClient && <LocationPermissionLine />}
            </div>
          </section>

          <section>
            <h2 className={SECTION_LABEL}>Appointment details</h2>
            <dl className="mt-2 flex flex-col gap-2 text-sm">
              {detailRows.map(({ label, value }) => (
                <div className="flex gap-3" key={label}>
                  <dt className={`w-20 shrink-0 ${MUTED}`}>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </section>

      {/* On a phone the notice and the checklist push the only button that
          matters past the fold, so it rides along the bottom instead. */}
      <div className="sticky bottom-0 z-10 -mx-5 mt-auto self-stretch border-t border-gray-200 bg-white/95 px-5 pb-safe-bottom pt-3 backdrop-blur dark:border-gray-800 dark:bg-gray-900/95 md:hidden">
        <div className="max-w-lg">
          {joinButton}
          {microphoneWarning}
        </div>
      </div>
    </div>
  );
}
